import type { ThemeName } from '../gfx/palette';

/**
 * Level file format (JSON).
 *
 * A level is a list of *areas*. The first area is the surface; extra areas are bonus
 * rooms reached through warps. Terrain is a rectangular block of ASCII rows — see
 * `src/world/tiles.ts` (LEGEND / ENTITY_LEGEND) for the character table.
 */

export interface WarpDef {
  /** Tile position of the trigger (top-left). */
  x: number;
  y: number;
  /** Trigger size in tiles (default 2 x 1). */
  w?: number;
  h?: number;
  /** How the player activates it. */
  enter: 'down' | 'right';
  /** Destination. */
  to: {
    area: string;
    /** Tile position the player appears at. */
    x: number;
    y: number;
    /** How the player emerges. */
    exit: 'up' | 'right' | 'drop';
  };
  /** Marks the warp that finishes a bonus room and returns to the surface. */
  label?: string;
}

export interface MoverDef {
  /** Tile position of the platform's home. */
  x: number;
  y: number;
  /** Width in tiles (height is always 1). */
  w: number;
  axis: 'x' | 'y';
  /** Travel distance in tiles. */
  range: number;
  /** Pixels per second. */
  speed: number;
  /** Starting phase 0..1. */
  phase?: number;
}

export interface DecorDef {
  type: string;
  /** Pixel position (so scenery can sit off the tile grid). */
  x: number;
  y: number;
  parallax?: number;
}

export interface AreaDef {
  id: string;
  theme: ThemeName;
  rows: string[];
  decor?: DecorDef[];
  movers?: MoverDef[];
  warps?: WarpDef[];
  /** Music track for this area. */
  music?: 'OVERWORLD' | 'CAVERN';
}

export interface CheckpointDef {
  /** Tile position where the player restarts. */
  x: number;
  y: number;
  area?: string;
}

export interface LevelDef {
  id: string;
  name: string;
  /** Starting countdown value. */
  time: number;
  /** Id of the area the player starts in. */
  start: string;
  /** Player spawn (tile coords) inside the start area. */
  spawn: { x: number; y: number };
  areas: AreaDef[];
  checkpoints?: CheckpointDef[];
  /** Seed for all randomised behaviour in this level. */
  seed?: number;
}

export function getArea(level: LevelDef, id: string): AreaDef {
  const a = level.areas.find((v) => v.id === id);
  if (!a) throw new Error(`level ${level.id}: unknown area '${id}'`);
  return a;
}

/** Basic structural validation — catches authoring mistakes early. */
export function validateLevel(level: LevelDef): string[] {
  const errors: string[] = [];
  if (!level.areas.length) errors.push('no areas');
  const ids = new Set<string>();
  for (const a of level.areas) {
    if (ids.has(a.id)) errors.push(`duplicate area id '${a.id}'`);
    ids.add(a.id);
    if (!a.rows.length) errors.push(`area '${a.id}' has no rows`);
    const w = a.rows[0]?.length ?? 0;
    for (let i = 0; i < a.rows.length; i++) {
      if (a.rows[i].length !== w) {
        errors.push(`area '${a.id}' row ${i} length ${a.rows[i].length} != ${w}`);
      }
    }
    for (const warp of a.warps ?? []) {
      if (!ids.has(warp.to.area) && !level.areas.some((v) => v.id === warp.to.area)) {
        errors.push(`area '${a.id}' warp targets unknown area '${warp.to.area}'`);
      }
    }
  }
  if (!ids.has(level.start)) errors.push(`start area '${level.start}' missing`);
  for (const cp of level.checkpoints ?? []) {
    if (cp.area && !ids.has(cp.area)) errors.push(`checkpoint in unknown area '${cp.area}'`);
  }
  if (level.time <= 0) errors.push('time must be positive');
  return errors;
}
